import { useCallback, useEffect, useState } from 'react';
import {
  createGuaranteeTicket,
  markTicketAccepted,
  markTicketFailed,
  type GuaranteeTicket,
} from '../core/models/GuaranteeTicket';
import { playClaimCodeFromPlayId } from '../core/rules/playClaimCode';
import { useOffline } from '../contexts/OfflineContext';

function upsertTicket(tickets: GuaranteeTicket[], next: GuaranteeTicket): GuaranteeTicket[] {
  const index = tickets.findIndex((ticket) => ticket.clientId === next.clientId);
  if (index === -1) return [next, ...tickets];
  const copy = [...tickets];
  copy[index] = next;
  return copy;
}

function ticketFromQueueAction(action: {
  clientId?: string;
  payload?: Record<string, unknown>;
}): GuaranteeTicket | null {
  const clientId = action.clientId ?? '';
  if (!clientId) return null;
  const payload = action.payload;
  if (!payload) return null;
  return createGuaranteeTicket({
    clientId,
    type: String(payload.type ?? ''),
    number: String(payload.number ?? ''),
    number2: payload.number2 ? String(payload.number2) : undefined,
    stake: Number(payload.stake ?? 0),
    status: 'pending',
    createdAt: Date.now(),
  });
}

function claimCodeFromFlushPlay(play: unknown, playId: string): string | undefined {
  if (play && typeof play === 'object' && 'claimCode' in play) {
    const code = (play as { claimCode?: unknown }).claimCode;
    if (typeof code === 'string' && code.trim()) return code.trim();
  }
  return playId ? playClaimCodeFromPlayId(playId) : undefined;
}

export function useGuaranteeTickets() {
  const { subscribeFlush, readQueue } = useOffline();
  const [tickets, setTickets] = useState<GuaranteeTicket[]>([]);

  const syncPendingFromQueue = useCallback(() => {
    const queued = readQueue().filter((action) => action.type === 'addPlay');
    setTickets((prev) => {
      let next = prev;
      for (const action of queued) {
        if (next.some((ticket) => ticket.clientId === action.clientId)) continue;
        const ticket = ticketFromQueueAction({
          clientId: action.clientId,
          payload: action.payload as Record<string, unknown> | undefined,
        });
        if (ticket) next = upsertTicket(next, ticket);
      }
      return next;
    });
  }, [readQueue]);

  useEffect(() => {
    syncPendingFromQueue();
  }, [syncPendingFromQueue]);

  useEffect(() => {
    return subscribeFlush((results) => {
      setTickets((prev) => {
        let next = prev;
        for (const result of results) {
          if (result.type !== 'addPlay') continue;
          const existing = next.find((ticket) => ticket.clientId === result.clientId);
          if (!existing) continue;
          const playId = String(result.play?.id ?? '');
          next = upsertTicket(
            next,
            result.ok
              ? markTicketAccepted(existing, playId, {
                acceptedAt: Date.now(),
                claimCode: claimCodeFromFlushPlay(result.play, playId),
              })
              : markTicketFailed(existing, result.rejectKey ?? result.error),
          );
        }
        return next;
      });
    });
  }, [subscribeFlush]);

  const trackPending = useCallback((ticket: GuaranteeTicket) => {
    setTickets((prev) => upsertTicket(prev, ticket));
  }, []);

  const trackAccepted = useCallback((
    ticket: GuaranteeTicket,
    serverPlayId: string,
    claimCode?: string,
  ) => {
    setTickets((prev) => upsertTicket(prev, markTicketAccepted(ticket, serverPlayId, {
      claimCode: claimCode || (serverPlayId ? playClaimCodeFromPlayId(serverPlayId) : undefined),
    })));
  }, []);

  const trackFailed = useCallback((ticket: GuaranteeTicket, rejectKey: string) => {
    setTickets((prev) => upsertTicket(prev, markTicketFailed(ticket, rejectKey)));
  }, []);

  return { tickets, trackPending, trackAccepted, trackFailed, syncPendingFromQueue };
}
